import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

const DEFAULTS = {
  companyName: '',
  registrationNumber: '',
  taxNumber: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  purpose: 'tender',
  urgency: 'standard',
};

const steps = ['Company Info', 'Contact & Purpose', 'Review'];

const TaxClearanceForm = ({ serviceId, onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (step === 1) {
      if (!form.companyName || !form.registrationNumber || !form.taxNumber) {
        toast({
          title: 'Required Fields Missing',
          description: 'Company name, registration number and tax number are required.',
          variant: 'destructive',
        });
        return false;
      }
    }
    if (step === 2) {
      if (!form.contactName || !form.contactEmail) {
        toast({
          title: 'Required Fields Missing',
          description: 'Contact name and email are required.',
          variant: 'destructive',
        });
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete(form);
  };

  return (
    <FormShell
      title="Tax Clearance Application"
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4" data-testid="tax-step-1">
          <h3 className="text-xl font-semibold text-emerald-800">Company Information</h3>
          <div>
            <Label>Registered Company Name *</Label>
            <Input
              data-testid="tax-company-name"
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>CIPC Registration Number *</Label>
              <Input
                data-testid="tax-reg-number"
                placeholder="e.g. 2023/123456/07"
                value={form.registrationNumber}
                onChange={(e) => set('registrationNumber', e.target.value)}
              />
            </div>
            <div>
              <Label>SARS Tax Number *</Label>
              <Input
                data-testid="tax-tax-number"
                value={form.taxNumber}
                onChange={(e) => set('taxNumber', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="tax-step-2">
          <h3 className="text-xl font-semibold text-emerald-800">Contact &amp; Purpose</h3>
          <div>
            <Label>Contact Person Full Name *</Label>
            <Input
              value={form.contactName}
              onChange={(e) => set('contactName', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={form.contactPhone}
                onChange={(e) => set('contactPhone', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Purpose of Tax Clearance</Label>
            <select
              value={form.purpose}
              onChange={(e) => set('purpose', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="tender">Tender application</option>
              <option value="good-standing">Good standing</option>
              <option value="foreign-investment">Foreign investment</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <Label>Urgency</Label>
            <select
              value={form.urgency}
              onChange={(e) => set('urgency', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="standard">Standard (1-7 days)</option>
              <option value="urgent">Urgent (24-48 hours)</option>
            </select>
          </div>
        </div>
      )}

      {step === 3 && (
        <Card className="bg-emerald-50 border-emerald-200" data-testid="tax-step-3">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review your Application</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Company:</strong> {form.companyName}</p>
            <p><strong>Reg No:</strong> {form.registrationNumber}</p>
            <p><strong>Tax No:</strong> {form.taxNumber}</p>
            <p><strong>Contact:</strong> {form.contactName} — {form.contactEmail}</p>
            <p><strong>Purpose:</strong> {form.purpose}</p>
            <p><strong>Urgency:</strong> {form.urgency}</p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default TaxClearanceForm;
